/* eslint-disable no-restricted-syntax */
import { injectable } from 'inversify';
import { parse, stringify } from 'query-string';
import { escape } from 'lodash';

import { FilterType } from '../filter';

@injectable()
export class QueryService {
  /** Преобразовать объект в query string (ключи предварительно сортируются)
   * При наличии у объекта свойства 'filters' типа object, развернуть каждое его поле дополнительно
   * @param query Объект
   * @returns {string}
   * */
  stringify(query: object, namespace = ''): string {
    const { filters, ...rest } = query as Record<string, unknown>;

    const stringifyRest = Object.entries(rest).reduce((acc, [key, value]) => {
      if (!namespace || key.includes('_param_') || key.includes('_filter_')) {
        return {
          ...acc,
          [key]: value,
        };
      }

      return {
        ...acc,
        [`${namespace}_param_${key}`]: value,
      };
    }, {});

    if (!filters || typeof filters !== 'object' || Array.isArray(filters)) {
      return stringify(stringifyRest, { arrayFormat: 'bracket' });
    }

    return stringify(
      {
        ...stringifyRest,
        ...Object.entries(filters).reduce((acc, [key, value]) => {
          return {
            ...acc,
            [namespace ? `${namespace}_filter_${key}` : `filter_${key}`]: value,
          };
        }, {}),
      },
      { arrayFormat: 'bracket' },
    );
  }

  /** Распарсить query string и преобразовать ее к типу объект
   * При этом поля, начинающиеся с 'filter_', будут объедены в поле 'filters' результативного объекта.
   * При этом символы в исходной строке “&“, “", ">“, '“', “'“, “`” будут конвертированы к соответствующим HTML-эквивалентам
   * @template T extends object
   * @param {string} query Строка, ожидающая преобразования
   * @return {T & { filters?: FilterType }}
   * */
  parse<T extends object>(
    query: string,
    namespace = '',
  ): T & { filters?: FilterType } {
    const entries = parse(query, { arrayFormat: 'bracket' });
    const PROPERTY_SEPARATOR = '.';

    const result: {
      filters?: FilterType;
      [key: string]: unknown;
    } = {};

    if (entries) {
      Object.entries(entries).forEach(([key, value]) => {
        let escapedValue: string | string[] = '';

        if (value && Array.isArray(value)) {
          escapedValue = value.map((fragment) =>
            escape(fragment ? fragment : undefined),
          );
        } else if (value && typeof value === 'string') {
          escapedValue = escape(value);
        }

        if (!escapedValue) {
          return;
        }

        if (namespace) {
          if (
            !key.startsWith(`${namespace}_filter_`) &&
            !key.startsWith(`${namespace}_param_`)
          ) {
            return;
          }
        }

        if (key.startsWith(namespace ? `${namespace}_filter_` : 'filter_')) {
          key = key.substring(
            (namespace ? `${namespace}_filter_` : 'filter_').length,
          );
          if (!result.filters) result.filters = {};

          if (key.includes(PROPERTY_SEPARATOR)) {
            const props = key.split(PROPERTY_SEPARATOR);

            //eslint-disable-next-line no-magic-numbers
            if (props.length > 2 || !['start', 'end'].includes(props[1])) {
              return;
            }

            let leaf = result.filters;

            for (let i = 0; i < props.length - 1; i++) {
              if (!leaf[props[i]]) leaf[props[i]] = {};
              leaf = leaf[props[i]] as FilterType;
            }

            leaf[props[props.length - 1]] = escapedValue as string;
          } else {
            result.filters[key] = value as string;
          }
        } else if (key.startsWith(`${namespace}_param_`)) {
          key = key.substring(`${namespace}_param_`.length);
          result[key] = value;
        } else {
          result[key] = value;
        }
      });
    }

    return result as T & { filters?: FilterType };
  }
}
